import React, { useState } from "react";
import {
  Alert,
  Box,
  Container,
  Typography,
  Button,
  Modal,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import NewPasswordLayout from "../../components/NewPasswordLayout";
import Loader from "../../components/Loader";
import Cookies from "js-cookie";
import { URLS } from "../../constants";
import api from "../../utils/api";
import { getLoggedinUserEmail } from "../../api/apiCore";

interface FormValues {
  oldpassword: string;
  password: string;
  confirmpassword: string;
}

const CreateNewPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState({
    oldpassword: false,
    password: false,
    confirmpassword: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [changepasswordError, setChangepasswordError] = useState<string | null>(
    null,
  );

  const resolver = yupResolver(
    yup.object().shape({
      oldpassword: yup.string().required(t("Please Enter Old Password.")),
      password: yup
        .string()
        .required(t("Please Enter New Password."))
        .min(8, t("Password must be at least 8 characters."))
        .matches(
          /[A-Z]/,
          t("Password must contain at least one uppercase letter."),
        )
        .matches(
          /[@$!%*?&#]/,
          t("Password must contain at least one special character."),
        )
        .notOneOf(
          [yup.ref("oldpassword")],
          t("New Password must be different from Old Password."),
        ),
      confirmpassword: yup
        .string()
        .oneOf([yup.ref("password")], t("Passwords don't match"))
        .required(t("This value is required.")),
    }),
  );

  const defaultValues: FormValues = {
    oldpassword: "",
    password: "",
    confirmpassword: "",
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver,
    context: { abortEarly: false },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const onSubmitForm: SubmitHandler<FormValues> = async values => {
    setLoading(true);
    try {
      const email = getLoggedinUserEmail();
      const response = await api.post("/api/change-password", {
        email: email,
        old_password: values.oldpassword,
        new_password: values.password,
      });

      if (response.status >= 200 && response.status < 300) {
        setPasswordChanged(true);
        setTimeout(() => {
          navigate("/auth-login");
        }, 3000);
      } else {
        const errorData = response.data;
        setChangepasswordError(
          errorData.message || "An error occurred while changing the password.",
        );
      }
    } catch (error) {
      setChangepasswordError("An error occurred while changing the password.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate("/auth-login");
  };

  const handleClickShowPassword = (field: keyof FormValues) => {
    setShowPassword(prevState => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  return (
    <NewPasswordLayout>
      <Container maxWidth="sm">
        <Box py={5}>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "#414042",
              marginBottom: "24px",
            }}
          >
            {t("Create New Password")}
          </Typography>
          {changepasswordError && (
            <Alert severity="error">{changepasswordError}</Alert>
          )}
          {passwordChanged && (
            <Alert severity="success">
              {t("Your password has been created successfully")}
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmitForm)} noValidate>
            {loading && <Loader />}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                {" "}
                <TextField
                  label={t("Old Password")}
                  type={showPassword.oldpassword ? "text" : "password"}
                  fullWidth
                  {...register("oldpassword")}
                  error={!!errors.oldpassword}
                  helperText={errors.oldpassword?.message || ""}
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleClickShowPassword("oldpassword")}
                        >
                          {showPassword.oldpassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    style: {
                      borderRadius: "8px",
                      backgroundColor: "#4140421A",
                    },
                  }}
                  sx={{
                    "& .MuiFilledInput-root": {
                      "&:hover": {
                        backgroundColor: "#4140421A",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#4140421A",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {" "}
                <TextField
                  label={t("New Password")}
                  type={showPassword.password ? "text" : "password"}
                  fullWidth
                  {...register("password")}
                  error={!!errors.password}
                  helperText={errors.password?.message || ""}
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleClickShowPassword("password")}
                        >
                          {showPassword.password ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    style: {
                      borderRadius: "8px",
                      backgroundColor: "#4140421A",
                    },
                  }}
                  sx={{
                    "& .MuiFilledInput-root": {
                      "&:hover": {
                        backgroundColor: "#4140421A",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#4140421A",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {" "}
                <TextField
                  label={t("Confirm New Password")}
                  type={showPassword.confirmpassword ? "text" : "password"}
                  fullWidth
                  {...register("confirmpassword")}
                  error={!!errors.confirmpassword}
                  helperText={errors.confirmpassword?.message || ""}
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            handleClickShowPassword("confirmpassword")
                          }
                        >
                          {showPassword.confirmpassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    style: {
                      borderRadius: "8px",
                      backgroundColor: "#4140421A",
                    },
                  }}
                  sx={{
                    "& .MuiFilledInput-root": {
                      "&:hover": {
                        backgroundColor: "#4140421A",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#4140421A",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Box textAlign="center" mt={4}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  {" "}
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#4C3699",
                      borderRadius: "24px",
                      fontWeight: "bold",
                      padding: "10px",
                      "&:hover": {
                        backgroundColor: "#36276F",
                      },
                    }}
                    type="submit"
                  >
                    {t("Save")}
                  </Button>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {" "}
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate("/auth-login")}
                    sx={{
                      borderRadius: "24px",
                      fontWeight: "bold",
                      borderColor: "#4C3699",
                      color: "#4C3699",
                      "&:hover": {
                        backgroundColor: "#EAEAFF",
                        borderColor: "#36276F",
                      },
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Box>
      </Container>

      <Modal open={showModal} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
          }}
        >
          <Typography variant="h6" component="h2">
            {t("Invalid Token")}
          </Typography>
          <Typography sx={{ mt: 2 }}>
            {t(
              "The token is invalid or missing. You will be redirected to the login page.",
            )}
          </Typography>
          <Box textAlign="right" mt={4}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#4C3699",
                "&:hover": {
                  backgroundColor: "#36276F",
                },
              }}
              onClick={handleModalClose}
            >
              {t("OK")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </NewPasswordLayout>
  );
};

export default CreateNewPassword;
